import React, { useState, useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useQuery } from 'react-query';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  AlertTypes,
  apiClient,
  setAlert,
  IAlertData,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@leap4/frontend';
import { MuiIcons } from '@leap4/frontend';
import { Server } from '@leap4/auth';
import { RootState } from '../../../interfaces/root-state.interface';
import { IDateRangeGState } from '../../../interfaces/datesGlobalState.interface';
import { baseBackendRoute } from '../../../config/generals';
import { OneOrgOnly } from '../../shared/OneOrgOnly/OneOrgOnly';
import {
  ReportHeader,
  MetricsSelector,
  DimensionsSelector,
  FiltersSection,
  ConstraintsSection,
  ReportResultsTable,
  TemplatesTab,
  ValidationPanel,
} from './components';
import type { ReportTabValue } from './components/ReportHeader';
import {
  IReportFilter,
  IReportConstraint,
  IReportResponse,
  IReportTemplate,
  IFilterOptions,
} from './report.interfaces';
import {
  DEFAULT_METRICS,
  DEFAULT_DIMENSIONS,
  DEFAULT_AGGREGATION,
  AGGREGATION_OPTIONS,
  DATE_RANGE_LIMITS,
} from './report.constants';

const reportsBaseUrl = `${baseBackendRoute}/reports`;

const mapStateToProps = (state: RootState) => ({
  dates: state.dates,
  selectedOrganization: state.auth.selectedOrganizationId,
});

const mapActions = {
  setAlert,
};

const connector = connect(mapStateToProps, mapActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface ReportPageProps extends PropsFromRedux {
  dates: IDateRangeGState;
  setAlert: (alert: IAlertData) => void;
}

const { Warning } = MuiIcons;

const calculateDaysDiff = (start: string, end: string): number => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

const validateDateRange = (
  start: string,
  end: string,
  aggregation: 'daily' | 'monthly' | 'sum'
): { isValid: boolean; message: string; limit: string } => {
  const daysDiff = calculateDaysDiff(start, end);
  const limit = DATE_RANGE_LIMITS[aggregation];

  if (daysDiff > limit.maxDays) {
    return {
      isValid: false,
      message: limit.message,
      limit: limit.label,
    };
  }

  return { isValid: true, message: '', limit: '' };
};

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 600,
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      fontSize: 11,
      display: 'block',
      textAlign: 'left',
    }}
  >
    {children}
  </Typography>
);

const TEMPLATES_STORAGE_KEY = 'xdash_report_templates';

const loadTemplatesFromStorage = (): IReportTemplate[] => {
  try {
    const raw = localStorage.getItem(TEMPLATES_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const saveTemplatesToStorage = (templates: IReportTemplate[]) => {
  localStorage.setItem(TEMPLATES_STORAGE_KEY, JSON.stringify(templates));
};

const ReportPage: React.FC<ReportPageProps> = ({ dates, setAlert }) => {
  const [activeTab, setActiveTab] = useState<ReportTabValue>('builder');
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>(DEFAULT_METRICS);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>(DEFAULT_DIMENSIONS);
  const [aggregationPeriod, setAggregationPeriod] = useState<'daily' | 'monthly' | 'sum'>(DEFAULT_AGGREGATION as 'daily' | 'monthly' | 'sum');
  const [filters, setFilters] = useState<IReportFilter[]>([]);
  const [constraints, setConstraints] = useState<IReportConstraint[]>([]);
  const [templates, setTemplates] = useState<IReportTemplate[]>(loadTemplatesFromStorage);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [shouldFetch, setShouldFetch] = useState(false);
  const [startDate, setStartDate] = useState<string>(dates.startDate || new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState<string>(dates.endDate || new Date().toISOString().split('T')[0]);
  const [dateRangeError, setDateRangeError] = useState<{ open: boolean; message: string; limit: string }>({
    open: false,
    message: '',
    limit: '',
  });

  useEffect(() => {
    saveTemplatesToStorage(templates);
  }, [templates]);

  const toggleMetric = useCallback((metric: string) => {
    setSelectedMetrics((prev) =>
      prev.includes(metric) ? prev.filter((m) => m !== metric) : [...prev, metric]
    );
  }, []);

  const toggleDimension = useCallback((dimension: string) => {
    setSelectedDimensions((prev) =>
      prev.includes(dimension) ? prev.filter((d) => d !== dimension) : [...prev, dimension]
    );
  }, []);

  const addFilter = useCallback(() => {
    setFilters((prev) => [
      ...prev,
      { id: Date.now(), field: '', mode: 'include', values: [] },
    ]);
  }, []);

  const removeFilter = useCallback((id: number) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const updateFilter = useCallback((id: number, key: keyof IReportFilter, value: any) => {
    setFilters((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const updated = { ...f, [key]: value };
        if (key === 'field') updated.values = [];
        return updated;
      })
    );
  }, []);

  const addConstraint = useCallback(() => {
    setConstraints((prev) => [
      ...prev,
      { id: Date.now(), metric: '', operator: '>', value: '' },
    ]);
  }, []);

  const removeConstraint = useCallback((id: number) => {
    setConstraints((prev) => prev.filter((c) => c.id !== id));
  }, []);

  const updateConstraint = useCallback((id: number, key: keyof IReportConstraint, value: any) => {
    setConstraints((prev) => prev.map((c) => (c.id === id ? { ...c, [key]: value } : c)));
  }, []);

  const addAllMetricConstraints = useCallback(() => {
    const existingMetrics = constraints.map((c) => c.metric);
    const newConstraints = selectedMetrics
      .filter((m) => !existingMetrics.includes(m))
      .map((m) => ({
        id: Date.now() + Math.random(),
        metric: m,
        operator: '>' as const,
        value: '',
      }));
    setConstraints((prev) => [...prev, ...newConstraints]);
  }, [selectedMetrics, constraints]);

  const fetchReportData = async (): Promise<IReportResponse> => {
    const activeFilters = filters
      .filter((f) => f.field && f.values.length > 0)
      .map((f) => ({
        field: f.field,
        operator: f.mode === 'include' ? 'in' : 'notIn',
        value: f.values,
      }));

    const response = await apiClient.post(`${reportsBaseUrl}/query`, {
      startDate,
      endDate,
      metrics: selectedMetrics,
      dimensions: selectedDimensions,
      aggregationPeriod,
      filters: activeFilters.length > 0 ? activeFilters : undefined,
    });
    return response.data;
  };

  const fetchFilterOptions = async (): Promise<IFilterOptions> => {
    const response = await apiClient.get(`${reportsBaseUrl}/filter-options`);
    return response.data;
  };

  const {
    data: reportData,
    isLoading,
    isFetching,
    refetch,
    remove,
  } = useQuery<IReportResponse>(
    ['report'],
    fetchReportData,
    {
      enabled: false,
      onError: (err: any) => {
        setAlert({ msg: err?.message || 'Failed to fetch report data', type: AlertTypes.FAILURE });
      },
      refetchOnWindowFocus: false,
    }
  );

  const isReportLoading = isLoading || isFetching;

  const {
    data: filterOptions,
    isLoading: filterOptionsLoading,
  } = useQuery<IFilterOptions>(
    ['reportFilterOptions'],
    fetchFilterOptions,
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const handleRunReport = useCallback(() => {
    if (selectedMetrics.length === 0) {
      setAlert({ msg: 'Please select at least one metric', type: AlertTypes.WARNING });
      return;
    }
    if (selectedDimensions.length === 0) {
      setAlert({ msg: 'Please select at least one dimension', type: AlertTypes.WARNING });
      return;
    }

    const validation = validateDateRange(startDate, endDate, aggregationPeriod);
    if (!validation.isValid) {
      setDateRangeError({
        open: true,
        message: validation.message,
        limit: validation.limit,
      });
      return;
    }

    remove();
    setShouldFetch(true);
    refetch();
  }, [selectedMetrics, selectedDimensions, startDate, endDate, aggregationPeriod, refetch, remove, setAlert]);

  const handleSaveTemplate = useCallback(() => {
    setTemplateName('');
    setSaveDialogOpen(true);
  }, []);

  const confirmSaveTemplate = useCallback(() => {
    const name = templateName.trim();
    if (!name) {
      setAlert({ msg: 'Please enter a template name', type: AlertTypes.WARNING });
      return;
    }

    const newTemplate: IReportTemplate = {
      id: `tpl-${Date.now()}`,
      name,
      createdAt: new Date().toISOString().split('T')[0],
      config: {
        startDate,
        endDate,
        metrics: selectedMetrics,
        dimensions: selectedDimensions,
        aggregationPeriod,
        filters,
        constraints,
      },
    };

    setTemplates((prev) => [newTemplate, ...prev]);
    setSaveDialogOpen(false);
    setTemplateName('');
    setAlert({ msg: `Template "${name}" saved successfully`, type: AlertTypes.SUCCESS });
  }, [templateName, startDate, endDate, selectedMetrics, selectedDimensions, aggregationPeriod, filters, constraints, setAlert]);

  const handleLoadTemplate = useCallback((template: IReportTemplate) => {
    setStartDate(template.config.startDate);
    setEndDate(template.config.endDate);
    setSelectedMetrics(template.config.metrics);
    setSelectedDimensions(template.config.dimensions);
    setAggregationPeriod(template.config.aggregationPeriod);
    setFilters(template.config.filters || []);
    setConstraints(template.config.constraints || []);
    setActiveTab('builder');
  }, []);

  const handleDeleteTemplate = useCallback((id: string) => {
    setTemplates((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const activeFilterCount = filters.filter((f) => f.field && f.values.length > 0).length;
  const activeConstraintCount = constraints.filter((c) => c.metric && c.value).length;

  const lastChosenOrganization = localStorage.getItem(Server.headers.X_ORGANZIZATION)?.split('-') || [];
  if (lastChosenOrganization.length > 1) {
    return <OneOrgOnly />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <ReportHeader
        activeTab={activeTab}
        onTabChange={setActiveTab}
        onSaveTemplate={handleSaveTemplate}
        onRunReport={handleRunReport}
        isRunning={isLoading}
      />

      <Box sx={{ p: 3 }}>

        {activeTab === 'validation' ? (
          <ValidationPanel />
        ) : activeTab === 'templates' ? (
          <TemplatesTab
            templates={templates}
            onLoadTemplate={handleLoadTemplate}
            onDeleteTemplate={handleDeleteTemplate}
          />
        ) : (
          <>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
              }}
            >
              {/* Date Range & Aggregation Row */}
              <Box sx={{ display: 'flex', gap: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box>
                  <SectionLabel>Date Range</SectionLabel>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    <TextField
                      type="date"
                      size="small"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                    />
                    <Typography color="text.secondary" sx={{ px: 0.5 }}>→</Typography>
                    <TextField
                      type="date"
                      size="small"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ width: 140, '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                    />
                  </Box>
                </Box>

                <Box>
                  <SectionLabel>Aggregation</SectionLabel>
                  <FormControl size="small" sx={{ minWidth: 120, mt: 1 }}>
                    <Select
                      value={aggregationPeriod}
                      onChange={(e) => setAggregationPeriod(e.target.value as 'daily' | 'monthly' | 'sum')}
                    >
                      {AGGREGATION_OPTIONS.map((opt) => (
                        <MenuItem key={opt.key} value={opt.key}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* Metrics */}
              <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <SectionLabel>Metrics</SectionLabel>
                <Box sx={{ mt: 1.5 }}>
                  <MetricsSelector selectedMetrics={selectedMetrics} onToggle={toggleMetric} />
                </Box>
              </Box>

              {/* Dimensions */}
              <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <SectionLabel>Dimensions</SectionLabel>
                <Box sx={{ mt: 1.5 }}>
                  <DimensionsSelector selectedDimensions={selectedDimensions} onToggle={toggleDimension} />
                </Box>
              </Box>

              {/* Filters */}
              <Box sx={{ py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <FiltersSection
                  filters={filters}
                  onAddFilter={addFilter}
                  onRemoveFilter={removeFilter}
                  onUpdateFilter={updateFilter}
                  filterOptions={filterOptions}
                  filterOptionsLoading={filterOptionsLoading}
                />
              </Box>

              {/* Constraints */}
              <Box sx={{ pt: 2 }}>
                <ConstraintsSection
                  constraints={constraints}
                  selectedMetrics={selectedMetrics}
                  onAddConstraint={addConstraint}
                  onRemoveConstraint={removeConstraint}
                  onUpdateConstraint={updateConstraint}
                  onAddAllMetricConstraints={addAllMetricConstraints}
                />
              </Box>
            </Paper>

            {(shouldFetch || isReportLoading) && (
              <Box sx={{ mt: 3 }}>
                <ReportResultsTable
                  data={reportData?.data || []}
                  totals={reportData?.totals || {}}
                  selectedMetrics={selectedMetrics}
                  selectedDimensions={selectedDimensions}
                  aggregationPeriod={aggregationPeriod}
                  isLoading={isReportLoading}
                  filterCount={activeFilterCount}
                  constraintCount={activeConstraintCount}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog
        open={dateRangeError.open}
        onClose={() => setDateRangeError({ open: false, message: '', limit: '' })}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <Warning sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Date Range Too Large
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {dateRangeError.message}
          </Typography>
          <Box
            sx={{
              bgcolor: 'warning.light',
              borderRadius: 1,
              p: 2,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Maximum allowed range: <strong>{dateRangeError.limit}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please reduce your date range or consider using a different aggregation level for larger time periods.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setDateRangeError({ open: false, message: '', limit: '' })}
            sx={{ textTransform: 'none', borderRadius: 1, px: 3 }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, p: 1 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            Save Template
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save your current report configuration as a reusable template.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Template Name"
            placeholder="e.g. Weekly Revenue Report"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmSaveTemplate(); }}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setSaveDialogOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmSaveTemplate}
            disabled={!templateName.trim()}
            sx={{ textTransform: 'none', borderRadius: 1.5, px: 3 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default connector(ReportPage);
